import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Input
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Search as SearchIcon, Clear as ClearIcon } from '@material-ui/icons';
import filter from 'lodash/filter';
import each from 'lodash/each';
import without from 'lodash/without';
import { useTranslation } from 'react-i18next';
import MultiSelect from './MultiSelect';

const selectOptions = [
  {
    multi: true,
    label: 'Status',
    options: [
      'Open',
      'Closed'
    ]
  },
  {
    multi: true,
    label: 'Disposition',
    options: [
      'In Review',
      'Approved',
      'Denied',
    ]
  },
  {
    multi: false,
    label: 'Time Range',
    options: [
      'Today',
      'Yesterday',
      'Last 7 Days',
      'Last 30 Days',
      'Last 60 Days',
      'Last 90 Days',
      'All Time'
    ]
  },
];
const KEYWORD_SEARCH_OPTION_KEYS = [
  'claimNumber',
  'claimant',
  'glassClaim.dealerName',
  'glassClaim.order.omcNumber',
  'glassClaim.order.shipperNumber',
  'glassClaim.order.orderNumber',
  'reason'
];
const FILTER_SECTION_MIN_HEIGHT = 50;
const KEYWORD_FADE_DURATION = 600;
const KEYWORD_INTERVAL_DURATION = 2200;

function cycleKeywordSearchOptions(currentIndex, optionCount) {
  if (!optionCount) {
    return 0;
  }
  return currentIndex % optionCount;
}

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: 10,
    border: '1px solid rgb(0,0,0,0.10)',
    boxShadow: '0px 2px 4px 0px rgb(0,0,0,0.07)',
    marginTop: theme.spacing(3)
  },
  searchInput: {
    marginLeft: theme.spacing(2),

    '& .MuiInputBase-input': {
      '&::-webkit-input-placeholder': {
        transition: 'color 0.4s'
      },
      '&::-moz-placeholder': {
        transition: 'color 0.4s'
      },
      '&::-ms-input-placeholder': {
        transition: 'color 0.4s'
      }
    }
  },
  placeholderTextBlank: {
    '& .MuiInputBase-input': {
      '&::-webkit-input-placeholder': {
        color: theme.palette.background.default
      },
      '&::-moz-placeholder': {
        color: theme.palette.background.default
      },
      '&::-ms-input-placeholder': {
        color: theme.palette.background.default
      }
    }
  },
  placeholderTextVisible: {
    '& .MuiInputBase-input': {
      '&::-webkit-input-placeholder': {
        color: theme.palette.text.secondary
      },
      '&::-moz-placeholder': {
        color: theme.palette.text.secondary
      },
      '&::-ms-input-placeholder': {
        color: theme.palette.text.secondary
      }
    }
  },
  chip: {
    margin: theme.spacing(1)
  },
  filters: {
    paddingTop: 0,
    paddingBottom: 0,
    minHeight: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    borderTop: '1px solid rgb(0,0,0,0.10)',
    borderBottom: '1px solid rgb(0,0,0,0.10)',
    transition: 'min-height 0.3s',
  }
}));

function Filter({ className, criteria, onUpdate, ...rest }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const keywordOptions = useMemo(
    () => KEYWORD_SEARCH_OPTION_KEYS.map(key => t(key)),
    [t]
  );
  const [inputValue, setInputValue] = useState('');
  const [keywordOptionIndex, setKeywordOptionIndex] = useState(0);
  const [isKeywordFading, setIsKeywordFading] = useState(false);
  const [chips, setChips] = useState(criteria.allFilters ?? []);
  let transitionFn;
  let keywordIntervalFn;
  let keywordTransitionFn;

  const filterRef = useRef();

  const handleInputChange = event => {
    event.persist();
    setInputValue(event.target.value);
  };

  const handleInputKeyup = event => {
    event.persist();

    if (event.keyCode === 13 && inputValue) {
      if (!chips.includes(inputValue)) {
        setChips(prevChips => [...prevChips, inputValue]);
        setInputValue('');
      }
    }
  };

  const handleClearChips = () => {
    setChips([]);
  }

  const handleChipDelete = chip => {
    setChips(prevChips => prevChips.filter(prevChip => chip !== prevChip));
  };

  const handleMultiSelectChange = (value) => {
    setChips(value);
  };

  const expandFilterSection = (shouldHeightTransition) => {
    filterRef.current.style.minHeight = FILTER_SECTION_MIN_HEIGHT + 'px';

    if (shouldHeightTransition) {
      transitionFn = setTimeout(() => {
        filterRef.current.style.height = null;
      }, 1000);
    }
  }

  const collapseFilterSection = () => {
    filterRef.current.style.minHeight = '0px';
    filterRef.current.style.height = '0px';
  }

  useEffect(() => {
    let status = selectOptions[0];
    let disposition = selectOptions[1];
    let timeRange = selectOptions[2];
    let keyWords = chips;

    status = filter(status.options, o => chips.indexOf(o) >= 0);
    disposition = filter(disposition.options, o => chips.indexOf(o) >= 0);
    timeRange = filter(timeRange.options, o => chips.indexOf(o) >= 0);

    each(status, o => {
      keyWords = without(keyWords, o);
    });
    each(disposition, o => {
      keyWords = without(keyWords, o);
    });
    each(timeRange, o => {
      keyWords = without(keyWords, o);
    });

    let hasActiveFilters = false;
    if (chips.length > 0) {
      hasActiveFilters = true;
    }

    onUpdate({
      status,
      keyWords,
      disposition,
      timeRange,
      hasActiveFilters,
      allFilters: chips
    });
  }, [chips]);

  useEffect(() => {
    if (chips.length > 0) {
      expandFilterSection(chips.length === 1);
    } else {
      collapseFilterSection();
    }
  }, [chips]);

  useEffect(() => {
    if (keywordOptions.length === 0) {
      return undefined;
    }

    keywordIntervalFn = setInterval(() => {
      setIsKeywordFading(true);
      keywordTransitionFn = setTimeout(() => {
        setIsKeywordFading(false);
        const nextIndex = cycleKeywordSearchOptions(keywordOptionIndex + 1, keywordOptions.length);
        setKeywordOptionIndex(nextIndex);
      }, KEYWORD_FADE_DURATION);
    }, KEYWORD_INTERVAL_DURATION);

    return () => {
      clearTimeout(transitionFn);
      clearTimeout(keywordTransitionFn);
      clearInterval(keywordIntervalFn);
    }
  }, [keywordOptionIndex, keywordOptions.length]);

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <Box px={2} py={1} display="flex" alignItems="center">
        <SearchIcon />
        <Input
          disableUnderline
          fullWidth
          className={clsx(
            classes.searchInput,
            isKeywordFading ? classes.placeholderTextBlank : classes.placeholderTextVisible,

          )}
          onChange={handleInputChange}
          onKeyUp={handleInputKeyup}
          placeholder={t('glassSearch.searchPlaceholder', { keyword: keywordOptions[keywordOptionIndex] ?? '' })}
          value={inputValue}
        />
      </Box>
      <Box
        style={{ height: 0 }}
        ref={filterRef}
        component="section"
        className={classes.filters}
        display="flex"
        alignItems="center"
        flexWrap="wrap"
      >
        {chips.map(chip => (
          <Chip
            className={classes.chip}
            key={chip}
            label={chip}
            onDelete={() => handleChipDelete(chip)}
          />
        ))}
      </Box>
      <Box display="flex" alignItems="center" flexWrap="wrap" px={2}>
        {selectOptions.map(option => (
          <MultiSelect
            {...option}
            key={option.label}
            onChange={handleMultiSelectChange}
            value={chips}
          />
        ))}
        <IconButton onClick={handleClearChips}>
          <ClearIcon />
        </IconButton>
      </Box>
    </Card>
  );
}

Filter.propTypes = {
  criteria: PropTypes.shape({
    status: PropTypes.arrayOf(PropTypes.string),
    disposition: PropTypes.arrayOf(PropTypes.string),
    timeRange: PropTypes.arrayOf(PropTypes.string),
    keyWords: PropTypes.arrayOf(PropTypes.string),
    hasActiveFilters: PropTypes.bool,
    allFilters: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default Filter;
